import { z } from "zod";
import {
  EvmAccountIdSchema,
  AssetIdSchema,
  PositiveIntegerAmountSchema,
} from "./requests.js";
import { lazySchema } from "@repo/lazy-schema";

/** Buying-power observation, not custody balances or funding equivalence. */
export const PortfolioCashFundingSchema = z
  .object({
    portfolioId: z.string().min(1),
    capturedAt: z.string().datetime(),
    expiresAt: z.string().datetime(),
    balances: z.array(
      z
        .object({
          chainRef: z.string().regex(/^eip155:[1-9]\d*$/),
          accountRef: EvmAccountIdSchema,
          assetId: AssetIdSchema,
          decimals: z.number().int().min(0).max(255),
          symbol: z.string().min(1),
          priceUsd: z.number().positive().optional(),
          liquidRaw: z.string().regex(/^\d+$/),
          redeemableRaw: z.string().regex(/^\d+$/),
          availableRaw: z.string().regex(/^\d+$/),
        })
        .strict()
        .superRefine((row, ctx) => {
          if (
            !row.accountRef.startsWith(`${row.chainRef}:`) ||
            !row.assetId.startsWith(`${row.chainRef}/`) ||
            BigInt(row.availableRaw) !==
              BigInt(row.liquidRaw) + BigInt(row.redeemableRaw)
          )
            ctx.addIssue({
              code: "custom",
              message: "Cash funding identity or budget mismatch",
            });
        }),
    ),
  })
  .strict()
  .superRefine((observation, ctx) => {
    const ids = observation.balances.map(
      (row) => `${row.accountRef}/${row.assetId}`,
    );
    if (
      new Set(ids).size !== ids.length ||
      Date.parse(observation.expiresAt) <= Date.parse(observation.capturedAt)
    )
      ctx.addIssue({
        code: "custom",
        message: "Cash funding observation is ambiguous or expired",
      });
  });
export type PortfolioCashFunding = z.infer<typeof PortfolioCashFundingSchema>;

const orderIdentity = lazySchema(() => ({
  // Converge derives child keys from this (`converge:<key>:pass:...`), and
  // stored converge keys cap at 256 characters.
  requestKey: z.string().trim().min(1).max(200),
  portfolioId: z.string().trim().min(1).max(256),
  /** The owner's Investing Account: funds buys, receives sale proceeds. */
  cashPortfolioId: z.string().trim().min(1).max(256),
}));

/**
 * Buy: move the chosen settlement cash from the Investing Account into the
 * portfolio, then converge it to the portfolio's own strategy.
 */
const PortfolioBuyOrderSchema = lazySchema(() =>
  z
    .object({
      side: z.literal("buy"),
      ...orderIdentity,
      legs: z
        .array(
          z
            .object({
              assetId: AssetIdSchema,
              amountRaw: PositiveIntegerAmountSchema,
            })
            .strict(),
        )
        .min(1)
        .max(8),
    })
    .strict(),
);

/**
 * Sell: converge the portfolio to its strategy on a smaller base, raising
 * `amount` in settlement cash, then transfer the settled proceeds back to
 * the Investing Account on each chain. `all` exits the portfolio entirely.
 */
const PortfolioSellOrderSchema = lazySchema(() =>
  z
    .object({
      side: z.literal("sell"),
      ...orderIdentity,
      amount: z.union([
        z
          .object({
            usd: z
              .string()
              .max(32)
              .regex(/^(?:0|[1-9]\d*)(?:\.\d{1,2})?$/)
              .refine((value) => Number(value) > 0, "must be positive"),
          })
          .strict(),
        z.object({ all: z.literal(true) }).strict(),
      ]),
    })
    .strict(),
);

/**
 * Owner-authorized portfolio order. The engine loads and compiles the
 * portfolio's own strategy; the caller only chooses the cash that moves.
 */
export const PortfolioOrderCreateSchema = lazySchema(() =>
  z
    .discriminatedUnion("side", [
      PortfolioBuyOrderSchema,
      PortfolioSellOrderSchema,
    ])
    .superRefine((value, ctx) => {
      if (value.portfolioId === value.cashPortfolioId)
        ctx.addIssue({
          code: "custom",
          path: ["cashPortfolioId"],
          message: "Investing Account and portfolio must differ",
        });
      if (value.side !== "buy") return;
      const seen = new Set<string>();
      for (const [index, leg] of value.legs.entries()) {
        const key = leg.assetId.toLowerCase();
        if (!key.startsWith("eip155:") || seen.has(key))
          ctx.addIssue({
            code: "custom",
            path: ["legs", index],
            message: "Legs must be distinct EVM assets",
          });
        seen.add(key);
      }
    }),
);
export type PortfolioOrderCreate = z.infer<typeof PortfolioOrderCreateSchema>;

/**
 * Service-authorized rebalance (scheduler, backend callers): converge the
 * portfolio to its own persisted strategy across its vault chains. No cash
 * moves in or out, so there is no Investing Account and nothing to choose;
 * the engine picks chains, routes and amounts.
 */
export const PortfolioRebalanceCreateSchema = lazySchema(() =>
  z
    .object({
      // Same cap as an order: converge child keys derive from it.
      requestKey: z.string().trim().min(1).max(200),
      portfolioId: z.string().trim().min(1).max(256),
    })
    .strict(),
);
export type PortfolioRebalanceCreate = z.infer<
  typeof PortfolioRebalanceCreateSchema
>;

const LiveDescriptorSchema = lazySchema(() =>
  z
    .object({
      transport: z.literal("websocket"),
      path: z.string(),
      url: z.string(),
      requiresToken: z.boolean(),
      token: z.string().optional(),
      expiresAt: z.string().optional(),
    })
    .passthrough(),
);

/**
 * A vault of the portfolio this order does not operate on, and why. Portfolio
 * orders converge on EVM chains only: an Investing Account's Solana vault, and
 * so the Cash it holds, never funds or receives one (`unsupported_vm`). The
 * other reasons are the engine's: the chain is not active for execution
 * (`chain_inactive`), has no settlement cash (`no_settlement_cash`), the
 * execution-account rule found nothing it can sign from (`no_account`, or
 * that rule's own reason such as `session_key_inactive`), or another vault
 * row on the same chain is the one that rule signs from (`not_selected`).
 */
export const PortfolioOrderExcludedAccountSchema = z.object({
  chainRef: z.string().min(1),
  address: z.string().min(1),
  reason: z.string().min(1),
});
export type PortfolioOrderExcludedAccount = z.infer<
  typeof PortfolioOrderExcludedAccountSchema
>;

export const PortfolioOrderSchema = lazySchema(() =>
  z.object({
    operationId: z.string().min(1),
    portfolioId: z.string().min(1),
    state: z.enum([
      "accepted",
      "running",
      "retrying",
      "awaiting_user",
      "completed",
      "failed",
      "cancelled",
    ]),
    headline: z.string(),
    detail: z.string().nullable(),
    phase: z.string().nullable(),
    pass: z.number().int().nullable(),
    maxPasses: z.number().int().nullable(),
    /**
     * How a COMPLETED order ended; null until then. `completed_with_drift`
     * means some value was held back (closed market, route limits, costly
     * bridge, a short sale): see `stopReasonCodes`, and `transferOut` for a
     * sell. Optional: additive to the original response.
     */
    outcome: z
      .enum(["converged", "completed_with_drift"])
      .nullable()
      .optional(),
    /** Typed reasons behind `completed_with_drift` (e.g. `delivery_short`). */
    stopReasonCodes: z.array(z.string()).optional(),
    /**
     * Sells only, once the proceeds settled: what the owner asked for and what
     * reached the Investing Account, in USD at the settled hub price.
     * "Sold $transferredUsd of $requestedUsd". Null for buys and until then.
     */
    transferOut: z
      .object({
        requested: z.union([
          z.object({ usd: z.string() }),
          z.object({ all: z.literal(true) }),
        ]),
        requestedUsd: z.string(),
        plannedUsd: z.string(),
        transferredUsd: z.string(),
        shortfallUsd: z.string(),
      })
      .nullable()
      .optional(),
    /**
     * The portfolio's vaults this order does not operate on, recorded at
     * admission (empty when it operates on every vault). A caller reads from
     * it which Cash the order could not use. Absent for orders admitted
     * before the engine recorded it. Optional: additive to the original
     * response.
     */
    excludedAccounts: z.array(PortfolioOrderExcludedAccountSchema).optional(),
  }),
);
export type PortfolioOrder = z.infer<typeof PortfolioOrderSchema>;

export const PortfolioOrderCreatedSchema = lazySchema(() =>
  PortfolioOrderSchema.extend({
    deduped: z.boolean(),
    portfolioLive: LiveDescriptorSchema,
  }),
);
export type PortfolioOrderCreated = z.infer<typeof PortfolioOrderCreatedSchema>;

export const PortfolioRebalanceCreatedSchema = lazySchema(() =>
  PortfolioOrderSchema.extend({
    deduped: z.boolean(),
    workflowId: z.string().min(1),
  }),
);
export type PortfolioRebalanceCreated = z.infer<
  typeof PortfolioRebalanceCreatedSchema
>;
